import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';

// Create the context
const AdminContext = createContext();

// Custom hook to use the admin context
export const useAdmin = () => useContext(AdminContext);

// Provider component
export const AdminProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adminUsers, setAdminUsers] = useState([]);
  const [error, setError] = useState(null);

  // Check if the current user is an admin
  useEffect(() => {
    const checkAdminStatus = async (user) => {
      console.log("Checking admin status for user:", user?.email);
      
      if (!user) {
        console.log("No user logged in");
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', user.uid);
        console.log("Fetching user document:", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          // Check if user has admin role
          const userData = userDoc.data();
          console.log("User document data:", userData);
          const isUserAdmin = userData.isAdmin === true;
          console.log("Is user admin?", isUserAdmin);
          setIsAdmin(isUserAdmin);
        } else {
          console.log("User document doesn't exist, creating one");
          // If user doc doesn't exist, create it with non-admin status
          await setDoc(userDocRef, {
            email: user.email,
            isAdmin: false,
            createdAt: new Date()
          });
          setIsAdmin(false);
        }
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError('Failed to verify admin status');
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };

    const unsubscribe = auth.onAuthStateChanged(checkAdminStatus);
    return () => unsubscribe();
  }, []);

  // Fetch all admin users
  const fetchAdminUsers = async () => {
    if (!isAdmin) {
      setError('Only admins can view the list of admins');
      return false;
    }

    try {
      console.log("Fetching admin users...");
      // Don't set loading state here to prevent flickering
      const adminsQuery = query(collection(db, 'users'), where('isAdmin', '==', true));
      const querySnapshot = await getDocs(adminsQuery);
      
      const admins = [];
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        admins.push({
          id: doc.id,
          email: userData.email,
          ...userData
        });
      });
      
      console.log("Found admin users:", admins.length);
      setAdminUsers(admins);
      return true;
    } catch (err) {
      console.error('Error fetching admin users:', err);
      setError('Failed to fetch admin users');
      return false;
    }
  };

  // Grant admin privileges to a user by email
  const grantAdminRole = async (email) => {
    if (!isAdmin) {
      setError('Only existing admins can grant admin privileges');
      return false;
    }

    try {
      setIsLoading(true);
      // Find user by email
      const usersQuery = query(collection(db, 'users'), where('email', '==', email));
      const querySnapshot = await getDocs(usersQuery);
      
      if (querySnapshot.empty) {
        setError(`No user found with email: ${email}`);
        return false;
      }

      // Update the first matching user (emails should be unique)
      const userDoc = querySnapshot.docs[0];
      await updateDoc(doc(db, 'users', userDoc.id), {
        isAdmin: true,
        adminGrantedAt: new Date(),
        adminGrantedBy: auth.currentUser.uid
      });

      // Refresh admin list
      await fetchAdminUsers();
      return true;
    } catch (err) {
      console.error('Error granting admin role:', err);
      setError('Failed to grant admin privileges');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Revoke admin privileges
  const revokeAdminRole = async (userId) => {
    if (!isAdmin) {
      setError('Only admins can revoke admin privileges');
      return false;
    }

    try {
      setIsLoading(true);
      await updateDoc(doc(db, 'users', userId), {
        isAdmin: false,
        adminRevokedAt: new Date(),
        adminRevokedBy: auth.currentUser.uid
      });

      // Refresh admin list
      await fetchAdminUsers();
      return true;
    } catch (err) {
      console.error('Error revoking admin role:', err);
      setError('Failed to revoke admin privileges');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    isAdmin,
    isLoading,
    error,
    adminUsers,
    fetchAdminUsers,
    grantAdminRole,
    revokeAdminRole
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;